import type { FileVersion } from '../../services/versions';

const pad = (n: number) => String(n).padStart(2, '0');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "14:20" today, "8 Oct" before today, "8 Oct 2025" in another year: how a list says when. */
export function whenSaved(iso: string, now = new Date()): { time: string; day: string | null } {
  const at = new Date(iso);
  const time = `${pad(at.getHours())}:${pad(at.getMinutes())}`;
  const sameDay = at.getFullYear() === now.getFullYear() && at.getMonth() === now.getMonth() && at.getDate() === now.getDate();
  if (sameDay) return { time, day: null };
  const day = `${at.getDate()} ${MONTHS[at.getMonth()]}${at.getFullYear() === now.getFullYear() ? '' : ` ${at.getFullYear()}`}`;
  return { time, day };
}

/**
 * A version's two lines, as the mock has them: its name (or its time), then who and when.
 * "Logo locked" / "8 Oct · Farah"; "11:05" / "Tarek"; "8 Oct, 11:05" / "Before restore".
 */
export function versionLines(version: FileVersion, now = new Date()): { title: string; detail: string } {
  const { time, day } = whenSaved(version.createdAt, now);
  const who = version.kind === 'restore' ? 'Before restore' : (version.author?.name ?? null);
  if (version.name) return { title: version.name, detail: [day ?? time, who].filter(Boolean).join(' · ') };
  return { title: day ? `${day}, ${time}` : time, detail: who ?? 'Saved on its own' };
}

/** Versions grouped for the panel: named ones first, then each day, newest first. */
export function versionGroups(versions: readonly FileVersion[], now = new Date()): { label: string; versions: FileVersion[] }[] {
  const named = versions.filter((one) => one.kind === 'named');
  const rest = versions.filter((one) => one.kind !== 'named');
  const groups: { label: string; versions: FileVersion[] }[] = [];
  if (named.length) groups.push({ label: 'Named', versions: named });
  for (const one of rest) {
    const { day } = whenSaved(one.createdAt, now);
    const label = day ?? 'Today';
    const last = groups[groups.length - 1];
    if (last && last.label === label && last.label !== 'Named') last.versions.push(one);
    else groups.push({ label, versions: [one] });
  }
  return groups;
}
